import { useState, useEffect } from 'react';
import { Target, Calendar, MapPin, Users, Clock, AlertCircle, Lock, Eye } from 'lucide-react';
import { useBackgrounds } from '../context/BackgroundContext';
import { useMember } from '../context/MemberContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const statusConfig = {
    active: { color: 'text-green-500', bg: 'bg-green-500/10', border: 'border-green-500/30', badge: 'badge-active' },
    planned: { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30', badge: 'badge-pending' },
    completed: { color: 'text-indigo-500', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', badge: 'badge-completed' }
};

export const Operations = () => {
    const { getBackground } = useBackgrounds();
    const { member, token } = useMember();
    const [operations, setOperations] = useState([]);
    const [filter, setFilter] = useState('all');
    
    const operationsBg = getBackground('operations');

    useEffect(() => {
        fetchOperations();
    }, [token]);

    const fetchOperations = async () => {
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${API}/operations`, { headers });
            setOperations(response.data);
        } catch (error) {
            console.error('Failed to fetch operations:', error);
        }
    };

    const filteredOperations = filter === 'all' 
        ? operations 
        : operations.filter(op => op.status === filter);

    const activeOps = operations.filter(op => op.status === 'active');
    const privateOps = operations.filter(op => op.visibility === 'private');

    return (
        <div className="min-h-screen bg-[#050505] pt-16" data-testid="operations-page">
            {/* Hero */}
            <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
                {operationsBg && (
                    <>
                        <div 
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${operationsBg})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
                    </>
                )}
                <div className="relative z-10 text-center px-4">
                    <span className="font-mono text-xs text-red-500 tracking-[0.3em]">TACTICAL OPERATIONS</span>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase mt-2">
                        Operations Center
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-4">
                        Mission Planning • Deployment Status • After Action Reports
                    </p>
                </div>
            </section>

            {/* Member Status Banner */}
            {!member && (
                <section className="px-4 py-4 bg-amber-500/10 border-y border-amber-500/20">
                    <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Lock className="w-5 h-5 text-amber-500" />
                            <span className="font-mono text-sm text-amber-200">
                                Some operations are classified. Login to view all intel.
                            </span>
                        </div>
                        <Link 
                            to="/member" 
                            className="btn-primary py-2 px-4 text-sm"
                            data-testid="member-login-link"
                        >
                            Member Login
                        </Link>
                    </div>
                </section>
            )}

            {/* Logged in member banner */}
            {member && (
                <section className="px-4 py-4 bg-green-500/10 border-y border-green-500/20">
                    <div className="max-w-6xl mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Eye className="w-5 h-5 text-green-500" />
                            <span className="font-mono text-sm text-green-200">
                                Welcome, <strong>{member.callsign}</strong>. Full operational access granted.
                            </span>
                        </div>
                        <span className="font-mono text-xs text-green-500/70 uppercase">
                            {member.rank}
                        </span>
                    </div>
                </section>
            )}

            {/* Active Operations Counter */}
            <section className="px-4 py-8 border-b border-white/10">
                <div className="max-w-6xl mx-auto">
                    <div className="tactical-panel p-6">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-4 h-4 rounded-full status-active animate-pulse" />
                                <div>
                                    <span className="font-mono text-xs text-gray-500 tracking-wider">OPERATIONS STATUS</span>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-mono text-4xl font-bold text-white" data-testid="active-ops-count">
                                            {activeOps.length.toString().padStart(2, '0')}
                                        </span>
                                        <span className="font-mono text-sm text-green-500 uppercase">Active</span>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-8">
                                <div className="text-center">
                                    <div className="font-mono text-2xl font-bold text-white">
                                        {operations.filter(op => op.status === 'planned').length}
                                    </div>
                                    <div className="font-mono text-xs text-amber-500 tracking-wider">PLANNED</div>
                                </div>
                                <div className="text-center">
                                    <div className="font-mono text-2xl font-bold text-white">
                                        {operations.filter(op => op.status === 'completed').length}
                                    </div>
                                    <div className="font-mono text-xs text-indigo-500 tracking-wider">COMPLETED</div>
                                </div>
                                <div className="text-center">
                                    <div className="font-mono text-2xl font-bold text-white">
                                        {operations.length}
                                    </div>
                                    <div className="font-mono text-xs text-gray-500 tracking-wider">TOTAL</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter */}
            <section className="px-4 py-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-wrap items-center gap-2">
                        {['all', 'active', 'planned', 'completed'].map((status) => (
                            <button
                                key={status}
                                onClick={() => setFilter(status)}
                                className={`px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                                    filter === status
                                        ? 'bg-red-500 text-white'
                                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                }`}
                                data-testid={`filter-${status}`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Operations List */}
            <section className="px-4 py-8">
                <div className="max-w-6xl mx-auto">
                    <div className="grid gap-6">
                        {filteredOperations.map((operation) => {
                            const config = statusConfig[operation.status] || statusConfig.planned;
                            const isPrivate = operation.visibility === 'private';
                            
                            return (
                                <div 
                                    key={operation.id} 
                                    className={`tactical-panel p-6 ${config.border} border-l-4`}
                                    data-testid={`operation-${operation.id}`}
                                >
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <Target className={`w-5 h-5 ${config.color}`} />
                                                <span className="font-mono text-xs text-gray-500 tracking-wider">
                                                    {operation.code_name}
                                                </span>
                                                <span className={`badge ${config.badge}`}>
                                                    {operation.status}
                                                </span>
                                                {isPrivate && (
                                                    <span className="badge bg-purple-500/20 text-purple-400 border-purple-500/30 flex items-center gap-1">
                                                        <Lock className="w-3 h-3" />
                                                        Classified
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-heading font-bold text-xl text-white mb-2">
                                                {operation.name}
                                            </h3>
                                            <p className="font-body text-gray-400 mb-4">
                                                {operation.objective}
                                            </p>
                                            <div className="flex flex-wrap gap-4 text-sm">
                                                <span className="flex items-center gap-2 text-gray-500">
                                                    <MapPin className="w-4 h-4" />
                                                    {operation.system}
                                                </span>
                                                <span className="flex items-center gap-2 text-gray-500">
                                                    <Calendar className="w-4 h-4" />
                                                    {operation.start_date}
                                                    {operation.end_date && ` — ${operation.end_date}`}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="lg:text-right">
                                            <span className="font-mono text-xs text-gray-500 tracking-wider block mb-2">
                                                PARTICIPATING UNITS
                                            </span>
                                            <div className="flex flex-wrap lg:justify-end gap-2">
                                                {operation.participants.map((unit, i) => (
                                                    <span 
                                                        key={i}
                                                        className="bg-white/5 border border-white/10 px-3 py-1 font-mono text-xs text-gray-300"
                                                    >
                                                        {unit}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {filteredOperations.length === 0 && (
                            <div className="text-center py-12">
                                <AlertCircle className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                <p className="font-mono text-gray-500">No operations found</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};
